import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ActivityPanel, ActivityEvent } from './ActivityPanel';
import { formatDateTime } from '../lib/orgTimezone';

/**
 * EntityActivityFeed — per-entity activity feed for a record detail page.
 *
 * Reads the humanized audit trail for one record from
 * GET /api/activity/entity/:entityType/:entityId and renders it through
 * ActivityPanel. Drop it in as a detail-page tab body (Parcel, Client, ...).
 *
 * entityType is the audit_logs entity_type, which is the plural table name
 * ('parcels', 'clients', 'tax_bills', ...).
 *
 * Domain 02.2 — Data Integrity & Audit.
 */

interface EntityActivityEvent {
  id: string;
  timestamp: string;
  actor: string;
  action: string;
  field: string | null;
  source: string | null;
  message: string;
}

interface EntityActivityResponse {
  events: EntityActivityEvent[];
  limit: number;
  offset: number;
}

const kindForAction: Record<string, NonNullable<ActivityEvent['kind']>> = {
  create: 'success',
  update: 'info',
  delete: 'danger',
};

export interface EntityActivityFeedProps {
  entityType: string;
  entityId: string;
  title?: string;
  limit?: number;
}

export function EntityActivityFeed({
  entityType,
  entityId,
  title = 'Activity',
  limit = 50,
}: EntityActivityFeedProps) {
  const query = useQuery<EntityActivityResponse>({
    queryKey: ['entity-activity', entityType, entityId],
    queryFn: ({ signal }) =>
      api.get<EntityActivityResponse>(
        `/api/activity/entity/${entityType}/${entityId}?limit=${limit}`,
        signal,
      ),
    enabled: !!entityType && !!entityId,
  });

  if (query.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading activity…</p>;
  }

  if (query.error) {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        Failed to load activity. {(query.error as Error).message}
      </div>
    );
  }

  const events: ActivityEvent[] = (query.data?.events ?? []).map(ev => ({
    id: ev.id,
    kind: kindForAction[ev.action] ?? 'neutral',
    title: ev.message,
    description: ev.field ? ev.field.replace(/_/g, ' ') : undefined,
    timestamp: formatDateTime(ev.timestamp),
  }));

  return (
    <ActivityPanel
      title={title}
      events={events}
      emptyMessage="No activity recorded on this record yet."
    />
  );
}
